export default function OrderBiryaniDelivery() {
  const faqSchema = `{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@graph": [
      {
        "@type": "Question",
        "@id": "https://biryani2go.us/blog/order-biryani-delivery-takeout-omaha#restaurant-near-me-open-now",
        "name": "Is the Indian restaurant near me open now?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Biryani2Go is open Monday through Sunday, 11:00 AM to 10:00 PM for pickup and delivery across Omaha and Council Bluffs. You can check real-time order status on our menu page or contact us directly for the latest hours and location details."
        }
      },
      {
        "@type": "Question",
        "@id": "https://biryani2go.us/blog/order-biryani-delivery-takeout-omaha#biryani-delivery-time",
        "name": "How long does biryani delivery take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Delivery time depends on your distance from our location. Most deliveries in Omaha and Council Bluffs take 45 to 60 minutes from the time you order. Pickup is typically ready in 30-45 minutes. Times are estimated at checkout, and we hand-pack every order to ensure it arrives hot."
        }
      },
      {
        "@type": "Question",
        "@id": "https://biryani2go.us/blog/order-biryani-delivery-takeout-omaha#restaurant-near-me-within-0-2-mi",
        "name": "Do you deliver to my exact location?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Biryani2Go delivers across Omaha and Council Bluffs. Delivery fees and times vary based on distance from our location at 2750 S Preston Rd, STE 111, Omaha, NE 68131. At checkout, you'll see if we deliver to your address and the estimated delivery time."
        }
      },
      {
        "@type": "Question",
        "@id": "https://biryani2go.us/blog/order-biryani-delivery-takeout-omaha#dine-in-or-takeout",
        "name": "Can I dine in at Biryani2Go?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Biryani2Go specializes in pickup and delivery — we pack everything hot and ready to go. For pickup, order on our menu page, and it's ready when you arrive. For delivery, we bring it right to your door across Omaha and Council Bluffs."
        }
      }
    ]
  }`;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
      <div className="space-y-6 text-base md:text-lg leading-relaxed text-ink-2 max-w-[68ch]">
        <p>
          You're hungry for real Indian food — maybe it's lunch, maybe it's dinner, and you just want something fresh and delicious delivered or ready for pickup near you. If you've searched "restaurant near me open now" or "Indian food delivery," you know the problem: most places either don't deliver to you, or they deliver something that's been sitting under a heat lamp for hours. Biryani2Go is different.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          How to Order Biryani2Go for Pickup or Delivery
        </h2>
        <p>
          It's simple. Go to our <a href="/menu" className="text-ember font-semibold underline hover:no-underline">menu page</a>, browse what's available, add items to your order, and check out. You'll see:
        </p>
        <ul className="list-disc list-inside space-y-2 my-4">
          <li>Real-time estimated pickup time (usually 30-45 minutes)</li>
          <li>Whether we deliver to your address</li>
          <li>Estimated delivery time if you choose delivery</li>
          <li>Order total and payment options</li>
        </ul>
        <p>
          Everything is made fresh when you order — your biryani goes into a handi, gets sealed, and starts steaming. No pre-made dabbas, no reheating.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Restaurant Near Me — Omaha & Council Bluffs Delivery
        </h2>
        <p>
          Biryani2Go is located at 2750 S Preston Rd, STE 111, Omaha, NE 68131. We deliver to addresses within Omaha and Council Bluffs. If you're unsure whether your address is in our delivery zone, the menu page will tell you at checkout.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Pickup vs. Delivery — Which is Right for You?
        </h2>
        <div className="space-y-4 my-4">
          <div>
            <h3 className="font-semibold text-maroon mb-1">Pickup</h3>
            <p>
              Order now, it's ready in 30-45 minutes. You come pick it up hot. Best if you're nearby and want to save on delivery fees.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-maroon mb-1">Delivery</h3>
            <p>
              Order now, it arrives at your door in 45-60 minutes depending on distance. We hand-pack it to stay hot the whole way. Best if you want to eat at home without the drive.
            </p>
          </div>
        </div>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          What to Order for Pickup or Delivery
        </h2>
        <p>
          Everything on our menu travels well. Here's what we recommend:
        </p>
        <div className="space-y-4 my-4">
          <div>
            <h3 className="font-semibold text-maroon mb-1">Biryani</h3>
            <p>
              Our signature — sealed in a handi and steamed fresh. Chicken, lamb, mutton, or vegetarian options. Perfect hot, and stays warm for 20+ minutes in delivery containers.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-maroon mb-1">Dosas</h3>
            <p>
              Crispy rice-and-lentil crepes. Best enjoyed soon after arrival, but still good if they cool slightly.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-maroon mb-1">Samosas & Appetizers</h3>
            <p>
              Perfect to share. Made fresh and travel great.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-maroon mb-1">Sides & Combos</h3>
            <p>
              Build a full meal. Breads, chutneys, desserts — check the menu for current offerings.
            </p>
          </div>
        </div>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Restaurant Near Me Open Now — Our Hours
        </h2>
        <p>
          Biryani2Go is open:
        </p>
        <ul className="list-disc list-inside space-y-1 my-4">
          <li>Monday – Sunday: 11:00 AM to 10:00 PM</li>
        </ul>
        <p>
          Both pickup and delivery available during these hours. Check our <a href="/contact" className="text-ember font-semibold underline hover:no-underline">contact page</a> for real-time status or <a href="/menu" className="text-ember font-semibold underline hover:no-underline">place your order now</a>.
        </p>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Tips for the Best Pickup & Delivery Experience
        </h2>
        <ul className="list-disc list-inside space-y-2 my-4">
          <li><strong>Order ahead:</strong> Don't wait until you're starving — give us the 30-45 minutes we need to cook it fresh.</li>
          <li><strong>Customizations:</strong> Have allergies or preferences? Note them in the order or <a href="/contact" className="text-ember font-semibold underline hover:no-underline">reach out before you order</a>.</li>
          <li><strong>Keep it hot:</strong> If you're picking up, eat soon. If it's being delivered, make sure you're home to accept it while it's still steaming.</li>
          <li><strong>Check the menu:</strong> Offerings change with the season and availability. Always check <a href="/menu" className="text-ember font-semibold underline hover:no-underline">what's available now</a>.</li>
        </ul>

        <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon !mt-10 !mb-2">
          Frequently Asked Questions
        </h2>

        <div className="space-y-5">
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              Is the Indian restaurant near me open now?
            </h3>
            <p>
              Biryani2Go is open Monday through Sunday, 11:00 AM to 10:00 PM for pickup and delivery across Omaha and Council Bluffs. You can check real-time order status on our menu page or contact us directly for the latest hours and location details.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              How long does biryani delivery take?
            </h3>
            <p>
              Delivery time depends on your distance from our location. Most deliveries in Omaha and Council Bluffs take 45 to 60 minutes from the time you order. Pickup is typically ready in 30-45 minutes. Times are estimated at checkout, and we hand-pack every order to ensure it arrives hot.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              Do you deliver within 0.2 miles of my location?
            </h3>
            <p>
              Biryani2Go delivers across Omaha and Council Bluffs. Delivery fees and times vary based on distance from our location at 2750 S Preston Rd, STE 111, Omaha, NE 68131. At checkout, you'll see if we deliver to your address and the estimated delivery time. If your address is in our service area, we'll get it to you.
            </p>
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg text-maroon mb-1.5">
              Can I dine in at Biryani2Go?
            </h3>
            <p>
              Biryani2Go specializes in pickup and delivery — we pack everything hot and ready to go. For pickup, order on our menu page, and it's ready when you arrive. For delivery, we bring it right to your door across Omaha and Council Bluffs.
            </p>
          </div>
        </div>

        <p className="!mt-10 text-sm text-ink-2/80 italic">
          Ready to order? Head to our <a href="/menu" className="text-ember underline hover:no-underline">menu page now</a> to place your pickup or delivery order.
        </p>
      </div>
    </>
  );
}